/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any --
 * The report document and pcg-report core are loose-typed by design: the
 * core is vendored prototype JS with a hand-written surface .d.ts. */

import React, { ReactElement, useState } from "react"
import { isTimingReviewFeature, timingSourceRecordings, timingSourceSelectionSummary, timingSourceValue } from "./core"

/**
 * "Choose recordings to include" — the recordings a timing card derives its
 * value from, one row per recording the review accepted for Timing (the
 * prototype's timingSourceChecklistHTML, Heart Report v0.5.8). Each row
 * ticks the recording in or out of this card and shows its position, the
 * technical rationale (informational only) and its value in the card's
 * terms; the heading shows the range the ticked recordings span. A card
 * with one recording lists it without a checkbox: the whole card is
 * excluded instead. The last ticked recording cannot be unticked; the
 * refusal shows under the list (the prototype uses an alert).
 *
 * `onToggle` applies the change and answers with the reason it was refused,
 * or null; the owner re-renders after a change.
 */

const SINGLE_SOURCE_NOTE =
  "This is the only recording contributing to this card. Use Exclude below to remove the card from the report."

const slug = (value: string): string => value.replace(/[^a-z0-9]+/gi, "-").toLowerCase()

const HeartReportTimingSources = ({
  feature,
  editable,
  onToggle,
}: {
  feature: any
  editable: boolean
  onToggle: (recordingId: string, included: boolean) => string | null
}): ReactElement | null => {
  // A fresh object per refusal, so a repeated refusal still re-renders the
  // controlled checkbox back to the card's state
  const [refusal, setRefusal] = useState<{ text: string } | null>(null)
  const records = timingSourceRecordings(feature)
  if (!isTimingReviewFeature(feature) || records.length === 0) {
    return null
  }
  const name = String(feature.name)
  const cardId = slug(name)
  const rationaleOf = (source: any): string => String(source.technicalRationale || "Timing evidence available")
  const positionOf = (source: any): string => String(source.position || source.recordingId || "Recording")

  if (records.length === 1) {
    const source = records[0]
    return (
      <section className="phr-timing-sources" data-testid={`timing-sources-${name}`}>
        <div className="phr-timing-sources-head">
          <strong>Source recording</strong>
        </div>
        <div className="phr-timing-source-row phr-timing-source-row--single">
          <span className="phr-timing-source-label phr-timing-source-label--locked">
            <span className="phr-timing-source-position">{positionOf(source)}</span>
            <span className="phr-timing-source-rationale">{rationaleOf(source)}</span>
          </span>
          <span className="phr-timing-source-value">{timingSourceValue(feature, source)}</span>
        </div>
        <div className="phr-timing-sources-note">{SINGLE_SOURCE_NOTE}</div>
      </section>
    )
  }

  return (
    <section className="phr-timing-sources" data-testid={`timing-sources-${name}`}>
      <div className="phr-timing-sources-head">
        <strong>Choose recordings to include</strong>
        <span className="phr-timing-sources-summary" data-testid={`timing-sources-summary-${name}`}>
          {timingSourceSelectionSummary(feature)}
        </span>
      </div>
      <div className="phr-timing-sources-hint">
        Tick to include; untick to exclude from this card. Technical rationale is informational only.
      </div>
      {records.map((source: any) => {
        const recordingId = String(source.recordingId ?? "")
        const inputId = `timing-source-${cardId}-${slug(recordingId)}`
        return (
          <div className="phr-timing-source-row" key={recordingId}>
            <input
              type="checkbox"
              id={inputId}
              checked={source.included !== false}
              disabled={!editable}
              onChange={(event) => {
                const reason = onToggle(recordingId, event.target.checked)
                setRefusal(reason ? { text: reason } : null)
              }}
            />
            <label
              htmlFor={inputId}
              className={`phr-timing-source-label${editable ? "" : " phr-timing-source-label--locked"}`}
            >
              <span className="phr-timing-source-position">{positionOf(source)}</span>
              <span className="phr-timing-source-rationale">{rationaleOf(source)}</span>
            </label>
            <span className="phr-timing-source-value">{timingSourceValue(feature, source)}</span>
          </div>
        )
      })}
      {refusal ? (
        <div className="phr-timing-sources-refusal" role="alert">
          {refusal.text}
        </div>
      ) : null}
    </section>
  )
}

export default HeartReportTimingSources
